/**
 * Farmer Site — theme-driven header + footer chrome.
 *
 * Content-agnostic: the same header renders both live themes; only
 * CSS tokens change. Nav items come from `site.navigation` (populated
 * by the wizard from the chosen starter package).
 */
import { useState } from "react";
import { Plant, List, X, ShoppingCart, MapPin, Basket } from "@phosphor-icons/react";

export function FarmerSiteHeader({ site, activeSlug = "home", onNavClick }) {
  const [open, setOpen] = useState(false);
  const nav = (site.navigation || []).filter((n) => !n.hidden);
  return (
    <header
      className="sticky top-0 z-40 backdrop-blur border-b"
      style={{
        background: "color-mix(in oklab, var(--fs-panel) 92%, transparent)",
        borderColor: "var(--fs-hairline)",
      }}
      data-testid="fs-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center gap-6">
        <a href={`/f/${site.slug}`} className="flex items-center gap-3 min-w-0" data-testid="fs-header-brand">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
            style={{ background: "var(--fs-brand)", color: "#fff" }}>
            {site.logo_url
              ? <img src={site.logo_url} alt={site.farm_name} className="w-9 h-9 rounded-full object-cover" />
              : <Plant size={18} weight="fill" />}
          </div>
          <div className="min-w-0">
            <div className="text-lg font-semibold leading-none truncate"
              style={{ fontFamily: "var(--fs-display)", color: "var(--fs-ink)" }}>
              {site.farm_name}
            </div>
            {site.tagline && (
              <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--fs-muted)" }}>{site.tagline}</div>
            )}
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-6 ml-auto">
          {nav.map((n) => (
            <a
              key={n.page_id || n.slug}
              href={n.slug === "home" ? `/f/${site.slug}` : `/f/${site.slug}#${n.slug}`}
              onClick={onNavClick}
              className="text-sm hover:underline underline-offset-4"
              style={{
                color: activeSlug === n.slug ? "var(--fs-brand)" : "var(--fs-ink)",
                fontWeight: activeSlug === n.slug ? 600 : 500,
              }}
              data-testid={`fs-nav-${n.slug}`}
            >
              {n.title}
            </a>
          ))}
        </nav>

        <div className="ml-auto md:ml-0 flex items-center gap-2">
          {site.location?.city && (
            <span className="hidden md:inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border"
              style={{ borderColor: "var(--fs-hairline)", color: "var(--fs-muted)" }}>
              <MapPin size={11} weight="fill" style={{ color: "var(--fs-brand)" }} /> {site.location.city}
            </span>
          )}
          <a href="#contact" className="hidden md:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium"
            style={{ background: "var(--fs-brand)", color: "#fff" }}
            data-testid="fs-header-contact">
            <Basket size={12} weight="fill" /> Order
          </a>
          <button onClick={() => setOpen(true)} className="md:hidden p-2" aria-label="Menu"
            data-testid="fs-header-menu">
            <List size={22} weight="bold" style={{ color: "var(--fs-ink)" }} />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute right-0 top-0 h-full w-72 shadow-xl p-6 flex flex-col gap-3"
            style={{ background: "var(--fs-panel)" }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="ml-auto" aria-label="Close">
              <X size={22} weight="bold" style={{ color: "var(--fs-ink)" }} />
            </button>
            {nav.map((n) => (
              <a key={n.page_id || n.slug}
                href={n.slug === "home" ? `/f/${site.slug}` : `/f/${site.slug}#${n.slug}`}
                onClick={() => setOpen(false)}
                className="text-base py-2 border-b"
                style={{ color: "var(--fs-ink)", borderColor: "var(--fs-hairline)" }}
                data-testid={`fs-mobile-nav-${n.slug}`}>
                {n.title}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export function FarmerSiteFooter({ site }) {
  const social = site.social_links || {};
  const socials = Object.entries(social).filter(([, v]) => v);
  return (
    <footer className="mt-8 border-t" style={{ borderColor: "var(--fs-hairline)", background: "var(--fs-panel)" }}
      data-testid="fs-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-14 grid md:grid-cols-3 gap-8">
        <div>
          <div className="text-lg font-semibold" style={{ fontFamily: "var(--fs-display)", color: "var(--fs-ink)" }}>
            {site.farm_name}
          </div>
          {site.tagline && <div className="mt-1 text-sm" style={{ color: "var(--fs-muted)" }}>{site.tagline}</div>}
          {site.location?.country && (
            <div className="mt-3 text-sm" style={{ color: "var(--fs-ink)" }}>
              {[site.location.city, site.location.state, site.location.country].filter(Boolean).join(", ")}
            </div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono" style={{ color: "var(--fs-brand)" }}>Contact</div>
          <div className="mt-2 space-y-1 text-sm" style={{ color: "var(--fs-ink)" }}>
            {site.contact?.email && <a href={`mailto:${site.contact.email}`} className="block hover:underline">{site.contact.email}</a>}
            {site.contact?.phone && <a href={`tel:${site.contact.phone}`} className="block hover:underline">{site.contact.phone}</a>}
            {socials.map(([k, v]) => (
              <a key={k} href={v} target="_blank" rel="noreferrer" className="block hover:underline capitalize">{k}</a>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono" style={{ color: "var(--fs-brand)" }}>Powered by</div>
          <a href="/farmers" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
            style={{ color: "var(--fs-ink)" }}>
            <Plant size={14} weight="fill" style={{ color: "var(--fs-brand)" }} /> Farmers Pulse
          </a>
          <p className="mt-2 text-xs" style={{ color: "var(--fs-muted)" }}>
            Every farm has a story. Build yours at farmerspulse.com — a NaturePulse network site.
          </p>
        </div>
      </div>
      <div className="border-t px-5 md:px-10 py-4 text-xs flex items-center justify-between"
        style={{ borderColor: "var(--fs-hairline)", color: "var(--fs-muted)" }}>
        <span>© {new Date().getFullYear()} {site.farm_name}</span>
        <span>Farmers Pulse · <a href="https://naturepulse.net" className="hover:underline">NaturePulse network</a></span>
      </div>
    </footer>
  );
}
